import {
    Button,
    Card,
    CardHeader,
    FluentProvider,
    makeStyles,
    tokens,
    typographyStyles,
} from '@fluentui/react-components';
import { Dismiss24Regular, WrenchScrewdriverRegular } from '@fluentui/react-icons';
import * as React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useMcpPromotionDialogContext } from '../../../hooks/context/dialogs';
import { useCurrentFluent2Theme } from '../../../hooks/styling';

interface McpPromotionCardProps {
    onDismiss?: () => void;
}

const messages = defineMessages({
    closeButtonAriaLabel: {
        id: 'QuickActionCard_CloseButton_AriaLabel',
        defaultMessage: 'Close',
        description: 'Aria label for quick action card "Close" button',
    },
});

/**
 * Styles
 */

const useCardStyles = makeStyles({
    root: {
        width: '305px',
    },
    icon: {
        height: '32px',
        width: '32px',
    },
    title: typographyStyles.body1Strong,
    content: {
        ...typographyStyles.caption1,
        color: tokens.colorNeutralForeground3,
    },
});

/**
 * END Styles
 */

export const McpPromotionCard: React.FC<McpPromotionCardProps> = React.memo((props: McpPromotionCardProps) => {
    const { onDismiss } = props;

    // Intl hooks
    const { formatMessage } = useIntl();

    const theme = useCurrentFluent2Theme();
    const { openSurface } = useMcpPromotionDialogContext();

    // Style hooks
    const cardStyles = useCardStyles();

    // Callback hooks
    const onDismissHandler = React.useCallback(
        (
            event: React.MouseEvent<
                HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | HTMLSpanElement,
                MouseEvent
            >
        ) => {
            if (onDismiss) {
                // Block event from propagating up to the card's click handler so that you don't also open the dialog when you attempt to dismiss the card
                event.stopPropagation();

                onDismiss();
            }
        },
        [onDismiss]
    );

    const onCardClickHandler = React.useCallback(() => {
        openSurface({});
    }, [openSurface]);

    return (
        <FluentProvider theme={theme}>
            <Card onClick={onCardClickHandler} focusMode="off" className={cardStyles.root}>
                <CardHeader
                    image={<WrenchScrewdriverRegular className={cardStyles.icon} />}
                    header={
                        <div className={cardStyles.title}>
                            <FormattedMessage
                                id="QuickActionCard_McpPromotionActionTitle_Text"
                                defaultMessage="Try out Dev Box MCP Server!"
                                description="Title of the 'Dev Box MCP Server' quick action card"
                            />
                        </div>
                    }
                    action={
                        <Button
                            appearance="subtle"
                            aria-label={formatMessage(messages.closeButtonAriaLabel)}
                            icon={<Dismiss24Regular />}
                            onClick={onDismissHandler}
                        />
                    }
                />
                <div className={cardStyles.content}>
                    <FormattedMessage
                        id="QuickActionCard_McpPromotionActionSubtitle_Text"
                        defaultMessage="Add dev box tools to Github Copilot inside of Visual Studio Code."
                        description="Explanatory text for the 'MCP Promotion' quick action card"
                    />
                </div>
            </Card>
        </FluentProvider>
    );
});

export default McpPromotionCard;
